import { useCallback } from 'react';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import useClassificationStore from '../store/useClassificationStore';
import useModelManagementStore from '../../../modelManagement/store/useModelManagementStore';
import {
    handleGetOneAPI,
    handleDownloadClassificationDataAPI,
    handleSaveClassificationDataAPI,
    startClassificationTrainingAPI,
    saveClassificationTrainingConfigAPI,
} from '../api/classificationLabelApi';
import { useSwal } from '../../../../../components/Notifications/swal/SwalCom';
import { resolveMediaUrl } from '../../../../../utils/mediaUrl';
import { computeSelection, UNSTRUCTURED_SCOPE } from '../utils/selectionModel';

const useClassificationLabelHook = () => {
    const navigate = useNavigate();

    // Store states
    const {
        classes, setClasses, updateClassImages, removeClassImage, removeImageFromClasses, 
        removeImagesFromClasses, updateClassName, initializeClasses, addClasses, reduceClassesTo, addClassImage, 
        classCount, setClassCount, unstructuredImages, setunstructuredImages, 
        removeUnstructuredImage, removeUnstructuredImages, addUnstructuredImages, 
        selectedImage, setSelectedImage, selectedImages, setSelectedImages,
        selectionAnchor, setSelectionAnchor, selectionScope, setSelectionScope, dragOver, 
        setDragOver, activeDragClass, setActiveDragClass, showDeleteButton, 
        setShowDeleteButton, isUploading, setUploadStatus, incrementUploadProgress, 
        resetUploadStatus, uploadProgress, completedUploads, totalUploads, resetAll
    } = useClassificationStore();

    const { modelInfo, setModelInfo, setModelDataInfo, modelDataInfo } = useModelManagementStore();
    const { swalSuccess, swalFail, swalConfirm } = useSwal();

    // =========================================================================
    // UPLOAD HANDLERS
    // =========================================================================

    /**
     * Handles uploading unstructured images with progress tracking
     * @param {FileList} files - Array of image files to upload
     */
    const handleUnstructuredUpload = useCallback(async (files) => {
        const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
        
        // Set upload status
        setUploadStatus(true, imageFiles.length, 0);
        const newImages = [];

        for (const file of imageFiles) {
            // Simulate processing time for large files
            await new Promise(resolve => setTimeout(resolve, 50));

            const newImage = {
                id: Math.random().toString(36).substr(2, 9),
                file,
                name: file.name,
                url: file.size <= 1 * 1024 * 1024 ? URL.createObjectURL(file) : "",
                assignedClass: null,
            };
            newImages.push(newImage);
            incrementUploadProgress(); // Update progress
        }

        addUnstructuredImages(newImages);

        // Auto-select the first image if none is selected
        if (!selectedImage && newImages.length > 0) { 
            setSelectedImage(newImages[0]); 
        }

        // Reset upload status after a brief delay
        setTimeout(() => resetUploadStatus(), 500);
    }, [selectedImage, setUploadStatus, incrementUploadProgress, resetUploadStatus, 
        addUnstructuredImages, setSelectedImage]);

    /**
     * Handles uploading images to specific classes with progress tracking
     * @param {FileList} files - Array of image files to upload
     * @param {string} classId - Target class ID
     */
    const handleClassUpload = useCallback(async (files, classId) => {
        const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));

        // Set upload status
        setUploadStatus(true, imageFiles.length, 0);
        const newImages = [];

        for (const file of imageFiles) {
            // Simulate processing time for large files
            await new Promise(resolve => setTimeout(resolve, 50));

            const newImage = {
                id: Math.random().toString(36).substr(2, 9),
                file,
                name: file.name,
                url: URL.createObjectURL(file),
                assignedClass: classId,
            };
            newImages.push(newImage);
            incrementUploadProgress(); // Update progress
        }

        updateClassImages(classId, newImages);

        // Reset upload status after a brief delay
        setTimeout(() => resetUploadStatus(), 500);
    }, [setUploadStatus, incrementUploadProgress, resetUploadStatus, updateClassImages]);

    // =========================================================================
    // DRAG AND DROP HANDLERS
    // =========================================================================

    /**
     * Handles drag over event
     * @param {Event} e - Drag event
     * @param {string} classId - Optional class ID for class-specific drop zones
     */
    const handleDragOver = (e, classId = null) => {
        e.preventDefault();
        setDragOver(true);
        setActiveDragClass(classId);
    };

    /**
     * Handles drag leave event
     */
    const handleDragLeave = () => {
        setDragOver(false);
        setActiveDragClass(null);
    };

    /**
     * Handles drop event for both files and image moves
     * @param {Event} e - Drop event
     * @param {string} targetClass - Target class ID for class assignments
     */
    const handleDrop = (e, targetClass = null) => {
        e.preventDefault();
        setDragOver(false);
        setActiveDragClass(null);

        const imageIds = draggedImageIds(e.dataTransfer);

        // Handle file drop
        if (imageIds.length === 0) {
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                if (targetClass !== null) {
                    handleClassUpload(files, targetClass);
                } else {
                    handleUnstructuredUpload(files);
                }
            }
            return;
        }

        // Handle image move between sections. A single tile keeps the original
        // path (it auto-advances the preview, which is the fast labeling loop);
        // a multi-selection goes through the batch mover.
        if (imageIds.length === 1) {
            const image = findImageById(imageIds[0]);
            if (!image) return;
            if (targetClass !== null) {
                assignImageToClass(image, targetClass);
            } else {
                moveImageToUnstructured(image);
            }
            return;
        }

        if (targetClass !== null) {
            moveImagesToClass(imageIds, targetClass);
        } else {
            moveImagesToUnstructured(imageIds);
        }
    };

    // =========================================================================
    // IMAGE MANAGEMENT
    // =========================================================================

    /**
     * Finds image by ID across all sections (unstructured and classes)
     * @param {string} imageId - Image ID to find
     * @returns {Object|null} Found image or null
     */
    const findImageById = (imageId) => {
        // Check unstructured images first
        let image = unstructuredImages.find(img => img.id === imageId);
        if (image) return image;

        // Check class images
        for (const cls of classes) {
            image = cls.images.find(img => img.id === imageId);
            if (image) return image;
        }

        return null;
    };

    /**
     * Assigns image to a specific class
     * @param {Object} image - Image object to assign
     * @param {string} classId - Target class ID
     */
    const assignImageToClass = (image, classId) => {
        // Remove from current location
        if (image.assignedClass === null) {
            removeUnstructuredImage(image.id);
        } else {
            removeClassImage(image.assignedClass, image.id);
        }

        // Add to target class
        const updatedImage = { ...image, assignedClass: classId };
        addClassImage(classId, updatedImage);

        // If this was the selected image, auto-advance to next unstructured image
        if (selectedImage && selectedImage.id === image.id) {
            autoAdvanceToNextImage(image.id);
        }
    };

    /**
     * Moves image to unstructured section
     * @param {Object} image - Image object to move
     */
    const moveImageToUnstructured = (image) => {
        // Remove from current class
        if (image.assignedClass !== null) {
            removeClassImage(image.assignedClass, image.id);
        }
        // Add to unstructured
        const updatedImage = { 
            ...image, 
            assignedClass: null, 
            previousClass: image.assignedClass 
        };
        addUnstructuredImages([updatedImage]);
    };

    /**
     * Auto-advances to next image when current image is assigned to a class
     * @param {string} currentImageId - ID of the currently assigned image
     */
    const autoAdvanceToNextImage = (currentImageId) => {
        const currentIndex = unstructuredImages.findIndex(img => img.id === currentImageId);
        if (currentIndex !== -1 && unstructuredImages.length > 1) {
            // Find the next image in the list
            const nextIndex = (currentIndex + 1) % unstructuredImages.length;
            setSelectedImage(unstructuredImages[nextIndex]);
        } else {
            setSelectedImage(null);
        }
    };

    /**
     * Handles file input change for both unstructured and class uploads
     * @param {Event} e - File input event
     * @param {string} classId - Optional class ID for class-specific uploads
     */
    const handleFileInput = (e, classId = null) => {
        if (classId !== null) {
            handleClassUpload(e.target.files, classId);
        } else {
            handleUnstructuredUpload(e.target.files);
        }
    };

    /**
     * Left-click on an image tile. Selection and preview are ONE act here, which is
     * what makes Ctrl/Shift feel native: the modifiers are read straight off the
     * event so callers just forward it.
     *
     * @param {Object} image - Clicked image object
     * @param {MouseEvent|boolean} [event] - the click event. A bare boolean is still
     *   accepted so an older call site passing `e.shiftKey` keeps working.
     */
    const handleImageClick = (image, event) => {
        const modifiers = typeof event === 'boolean'
            ? { shift: event }
            : { ctrl: !!(event?.ctrlKey || event?.metaKey), shift: !!event?.shiftKey };
        applySelection(image, modifiers);
    };

    /**
     * Handles class assignment button click for selected image
     * @param {string} classId - Target class ID
     */
    const handleClassButtonClick = (classId) => {
        if (selectedImage) {
            assignImageToClass(selectedImage, classId);
        }
    };

    /**
     * Moves a whole set of images into a class in one go.
     *
     * Not a loop over assignImageToClass(): that auto-advances the preview on every
     * single move, so a batch would walk the preview through images it is busy
     * moving. Here the images are all resolved FIRST (against the pre-move lists),
     * moved, and the preview settled once at the end.
     *
     * @param {Array<string>} imageIds
     * @param {string|number} classId
     */
    const moveImagesToClass = (imageIds, classId) => {
        const images = imageIds
            .map(findImageById)
            .filter(Boolean)
            .filter(img => String(img.assignedClass) !== String(classId));
        if (images.length === 0) return;

        images.forEach(img => {
            if (img.assignedClass === null || img.assignedClass === undefined) {
                removeUnstructuredImage(img.id);
            } else {
                removeClassImage(img.assignedClass, img.id);
            }
            addClassImage(classId, { ...img, assignedClass: classId });
        });

        settlePreviewAfterMove(new Set(images.map(img => img.id)));
        clearSelection();
    };

    /**
     * Moves a whole set of images back to the unlabeled queue. Mirror of
     * moveImagesToClass — see the note there about why this is not a loop.
     *
     * @param {Array<string>} imageIds
     */
    const moveImagesToUnstructured = (imageIds) => {
        const images = imageIds
            .map(findImageById)
            .filter(Boolean)
            .filter(img => img.assignedClass !== null && img.assignedClass !== undefined);
        if (images.length === 0) return;

        images.forEach(img => removeClassImage(img.assignedClass, img.id));
        addUnstructuredImages(images.map(img => ({
            ...img,
            assignedClass: null,
            previousClass: img.assignedClass
        })));

        settlePreviewAfterMove(new Set(images.map(img => img.id)));
        clearSelection();
    };

    /**
     * After a batch move, leave the preview on the next image still in the queue
     * rather than on one that just left it.
     *
     * @param {Set<string>} movedIds
     */
    const settlePreviewAfterMove = (movedIds) => {
        if (!selectedImage || !movedIds.has(selectedImage.id)) return;
        const next = unstructuredImages.find(img => !movedIds.has(img.id));
        setSelectedImage(next ?? null);
    };

    /**
     * Starts a drag. When the grabbed tile is part of a multi-selection the WHOLE
     * selection travels — without this, building a selection and dragging it moves
     * exactly one image and silently leaves the rest behind.
     *
     * @param {DragEvent} e
     * @param {Object} image - the tile being dragged
     */
    const handleImageDragStart = (e, image) => {
        const ids = selectedImages.has(image.id) && selectedImages.size > 1
            ? Array.from(selectedImages)
            : [image.id];
        e.dataTransfer.setData('imageIds', JSON.stringify(ids));
        // kept for any drop target still reading the single-id payload
        e.dataTransfer.setData('imageId', image.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    /**
     * The ids a drop is carrying: the multi payload when present, else the single
     * one, else nothing (which means files were dropped, not tiles).
     *
     * @param {DataTransfer} dataTransfer
     * @returns {Array<string>}
     */
    const draggedImageIds = (dataTransfer) => {
        try {
            const many = dataTransfer.getData('imageIds');
            if (many) {
                const parsed = JSON.parse(many);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (err) {
            // fall through to the single-id payload
        }
        const one = dataTransfer.getData('imageId');
        return one ? [one] : [];
    };

    // =========================================================================
    // SELECTION MANAGEMENT
    // =========================================================================

    /**
     * The ordered list an image currently lives in, and the scope key naming it.
     *
     * A Shift range only means something inside ONE list, and a bulk assign is only
     * unambiguous when every selected image comes from the same place, so every
     * selection is tagged with the list it was made in.
     *
     * @param {Object} image
     * @returns {{key: string|number|null, items: Array}}
     */
    const listFor = (image) => {
        if (!image) return { key: null, items: [] };
        if (image.assignedClass === null || image.assignedClass === undefined) {
            return { key: UNSTRUCTURED_SCOPE, items: unstructuredImages };
        }
        const cls = classes.find(c => String(c.id) === String(image.assignedClass));
        return { key: image.assignedClass, items: cls?.images ?? [] };
    };

    /**
     * The one place selection is decided, for every input that can change it.
     *
     * Follows the conventions every file manager has trained users on, so nothing
     * here needs explaining:
     *
     *   click                 select just this one (drops the rest)
     *   Ctrl / Cmd + click    add or remove this one
     *   Shift + click         select the range from the anchor to here
     *   Ctrl + Shift + click  add that range to what is already selected
     *   right click           add or remove (+ Shift for an additive range)
     *
     * Clicking inside a different list starts a fresh selection there rather than
     * silently mixing two lists — see listFor().
     *
     * @param {Object} image - the image that was clicked
     * @param {{ctrl?: boolean, shift?: boolean, preview?: boolean}} modifiers
     */
    const applySelection = (image, { ctrl = false, shift = false, preview = true } = {}) => {
        if (!image) return;
        const { key, items } = listFor(image);
        const next = computeSelection({
            items,
            imageId: image.id,
            listKey: key,
            selected: selectedImages,
            anchor: selectionAnchor,
            scope: selectionScope,
            ctrl,
            shift,
        });

        setSelectedImages(next.selected);
        setSelectionAnchor(next.anchor);
        setSelectionScope(next.scope);
        if (preview) setSelectedImage(image);
    };

    /**
     * Right-click selection. Kept as its own entry point because it must behave
     * exactly as it always has (toggle, or an additive range with Shift) and must
     * NOT move the preview — only the left button does that.
     *
     * @param {string} imageId - Image ID to toggle
     * @param {boolean} shiftKey - Whether shift key was pressed
     */
    const toggleImageSelection = (imageId, shiftKey = false) => {
        applySelection(findImageById(imageId), { ctrl: true, shift: shiftKey, preview: false });
    };

    /**
     * Assigns multiple selected images to a class
     * @param {string} classId - Target class ID
     */
    const assignMultipleToClass = (classId) => {
        if (selectedImages.size === 0) return;
        // Deliberately NOT limited to unlabeled images: now that a class grid can be
        // multi-selected too, picking a class has to be able to move a batch out of
        // the class it is in. moveImagesToClass skips only what is already there.
        moveImagesToClass(Array.from(selectedImages), classId);
    };

    /**
     * Selects every image in the list the selection is currently in — the unlabeled
     * queue when there is no selection yet, so Ctrl+A does the obvious thing on a
     * fresh screen. (Ctrl+A while a class grid is selected takes that whole class.)
     */
    const selectAllUnstructured = () => {
        const scope = selectionScope ?? UNSTRUCTURED_SCOPE;
        const items = scope === UNSTRUCTURED_SCOPE
            ? unstructuredImages
            : (classes.find(c => String(c.id) === String(scope))?.images ?? []);
        if (items.length === 0) return;

        setSelectedImages(new Set(items.map(img => img.id)));
        setSelectionScope(scope);
        setSelectionAnchor(items[0].id);
    };

    /**
     * Clears all image selections, including the anchor and scope — otherwise a
     * later Shift+click would measure a range from an image nobody selected.
     */
    const clearSelection = () => {
        setSelectedImages(new Set());
        setSelectionAnchor(null);
        setSelectionScope(null);
    };

    // =========================================================================
    // CLASS MANAGEMENT
    // =========================================================================

    /**
     * Updates class name
     * @param {string} classId - Class ID to update
     * @param {string} newName - New class name
     */
    const handleClassNameChange = (classId, newName) => {
        updateClassName(classId, newName);
    };

    /**
     * Initializes classes based on class count (fresh start when no classes)
     */
    const handleInitializeClasses = () => {
        initializeClasses(classCount);
    };

    /**
     * Adds new classes without resetting existing ones (preserves all data)
     */
    const handleAddClasses = () => {
        addClasses();
    };

    /**
     * Reduces classes to target count (removes from end; images in removed classes are lost)
     */
    const handleReduceClasses = (targetCount) => {
        reduceClassesTo(targetCount);
    };

    // =========================================================================
    // DELETE OPERATIONS
    // =========================================================================

    /**
     * Deletes selected images from both unstructured and class sections
     */
    const deleteSelectedImages = () => {
        if (selectedImages.size === 0) return;

        // Delete from unstructured
        removeUnstructuredImages(selectedImages);

        // Delete from classes
        removeImagesFromClasses(selectedImages);
    };

    /**
     * Deletes a single image
     * @param {string} imageId - Image ID to delete
     * @param {Event} e - Click event
     */
    const deleteImage = (imageId, e) => {
        e.stopPropagation();
        
        // Remove from unstructured and classes
        removeUnstructuredImage(imageId);
        removeImageFromClasses(imageId);

        // Update selection if needed
        if (selectedImages.has(imageId)) {
            const newSelection = new Set(selectedImages);
            newSelection.delete(imageId);
            setSelectedImages(newSelection);

            if (selectedImage && selectedImage.id === imageId) {
                setSelectedImage(newSelection.size > 0 ? 
                    findImageById(Array.from(newSelection)[0]) : null);
            }
        }
    };

    // =========================================================================
    // DATA PERSISTENCE
    // =========================================================================

    /**
     * Saves classification data to backend
     */
    const handleSaveClassificationData = async (options = {}) => {
        const {
            forceUpdateExisting = false,
            silent = false,
            skipNavigate = false,
        } = options;
        const { classes, unstructuredImages } = useClassificationStore.getState();

        let isNewVersion = true;

        const hasClassImages = Array.isArray(classes) && classes.some((c) => Array.isArray(c.images) && c.images.length > 0);
        const hasUnstructuredImages = Array.isArray(unstructuredImages) && unstructuredImages.length > 0;
        if (!hasClassImages && !hasUnstructuredImages) {
            swalFail("No images", "Please upload or assign at least one image before saving.");
            return;
        }

        // Check if versions exist and > 0
        if (forceUpdateExisting) {
            const existingId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id;
            if (!existingId) {
                const message = "No existing dataset version found to update. Please create or load a version first.";
                if (!silent) {
                    swalFail("Unavailable", message);
                }
                throw new Error(message);
            }
            isNewVersion = false;
        } else if (modelDataInfo?.version || (modelInfo?.versions && modelInfo.versions.length > 0)) {
            const result = await new Promise((resolve) => {
                swalConfirm(
                    "Create New Version?",
                    "Select OK to create a new dataset version, or Cancel to update the existing one.",
                    resolve,
                );
            });

            if (result?.isConfirmed) {
                isNewVersion = true;
            } else if (result?.dismiss === 'cancel') {
                isNewVersion = false;
            } else {
                return;
            }
        } else {
            console.log("No existing versions, proceeding to save as new version.");
        }

        const formData = new FormData();
        formData.append("project_id", modelInfo?.projectId || modelInfo?.project_id);
        formData.append("model_id", modelInfo?._id || modelInfo?.model_id);
        formData.append("model_type", modelInfo?.modelType || modelInfo?.model_type);
        formData.append("owner_id", modelInfo?.ownerId || modelInfo?.owner_id);

        const targetModelDataInfoId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id || null;
        if (!isNewVersion && !targetModelDataInfoId) {
            const message = "No existing dataset version found to update. Please create a new version first.";
            if (!silent) {
                swalFail("Unavailable", message);
            }
            throw new Error(message);
        }

        formData.append("is_new_version", !!isNewVersion);
        formData.append("model_data_info_id", isNewVersion ? null : targetModelDataInfoId);

        // Classes metadata
        formData.append(
            "classes",
            JSON.stringify(
                classes.map((c) => ({
                    id: c.id,
                    name: c.name,
                    images: c.images.map((img) => ({
                        id: img.id,
                        name: img.name,
                        assignedClass: c.id,
                        url: !!img.file ? null : img?.url,
                        thumbnailUrl: !!img.file ? null : img?.thumbnailUrl,
                        isNew: !!img.file,
                    })),
                }))
            )
        );

        // Unstructured metadata
        formData.append(
            "unstructured_images",
            JSON.stringify(
                unstructuredImages.map((img) => ({
                    id: img.id,
                    name: img.name,
                    assignedClass: img.assignedClass,
                    url: !!img.file ? null : img?.url,
                    thumbnailUrl: !!img.file ? null : img?.thumbnailUrl,
                    isNew: !!img.file,
                }))
            )
        );

        // Attach only new files
        classes.forEach((cls) => {
            cls.images.forEach((img) => {
                if (img.file) {
                    formData.append("class_files", img.file, `${cls.id}_${img.name}`);
                }
            });
        });

        unstructuredImages.forEach((img) => {
            if (img.file) {
                formData.append("unstructured_files", img.file, img.name);
            }
        });

        try {
            const response = await handleSaveClassificationDataAPI(formData);
            const savedId = response?.data?.model_info_id || targetModelDataInfoId || null;
            if (modelDataInfo) {
                modelDataInfo._id = savedId || modelDataInfo?._id;
            }

            if (!silent) {
                swalSuccess("Saved!", "Classification data saved successfully.");
            }
            if (!skipNavigate) {
                navigate("/ModelManagement", { 
                    state: { projectId: modelInfo?.projectId || modelInfo?.project_id } 
                });
            }
            return savedId;
        } catch (error) {
            console.error("Error saving classification data", error);
            const message = error?.response?.data?.message || "Failed to save classification data.";
            if (!silent) {
                swalFail("Error", message);
            }
            throw new Error(message);
        }
    };

    /**
     * Fetches classification data from backend
     * @param {Object} params - Query parameters
     */
    const handleGetOneClassificationData = async (params) => {
        try {
            const response = await handleGetOneAPI(params);
            const reportData = response?.data || {};
            const model = reportData?.model || {};
            const resModelDataInfo = reportData?.model_info || {};
            const classes = reportData?.model_info?.classes || [];
            const items = reportData?.model_data_items_info || [];

            setModelInfo(model);
            setModelDataInfo(resModelDataInfo);

            // Get unstructured images (classId == "0")
            const unstructuredImages = items
                .filter((item) => String(item.classId) === "0")
                .map((item) => ({
                    id: item.imageId,
                    assignedClass: null,
                    file: null,
                    name: item.name,
                    url: item.url,
                    thumbnailUrl: item.thumbnailUrl,
                    url_display: resolveMediaUrl(item?.url_display),
                    thumbnail_url_display: resolveMediaUrl(item?.thumbnail_url_display)
                }));

            // Build classes with images
            const transformedClasses = classes.map((cls) => {
                const classIdStr = String(cls.id);

                const images = items
                    .filter((item) => String(item.classId) === classIdStr)
                    .map((item) => ({
                        id: item.imageId,
                        assignedClass: cls.id,
                        file: null,
                        name: item.name,
                        url: item.url,
                        thumbnailUrl: item.thumbnailUrl,
                        url_display: resolveMediaUrl(item?.url_display),
                        thumbnail_url_display: resolveMediaUrl(item?.thumbnail_url_display)
                    }));

                return {
                    ...cls,
                    images,
                };
            });

            console.log("Transformed Classes:", transformedClasses);
            console.log("Unstructured Images:", unstructuredImages);

            setClasses(transformedClasses);
            setunstructuredImages(unstructuredImages);
        } catch (error) {
            console.error("Error fetching summary report:", error);
        }
    };

    /**
     * Resets all classification data
     */
    const handleResetClsData = () => {
        resetAll();
    };

    /**
     * Downloads classification data as ZIP file
     */
    const handleDownloadClassificationData = async () => {
        try {
            const savedId = await handleSaveClassificationData();
            if (!savedId) {
                return { status: 'error', message: 'Unable to determine dataset version.' };
            }
            const model_data_info_id = savedId || modelDataInfo?._id || 'new_dataset';
            const params = { model_data_info_id };

            // Call API to download ZIP
            const response = await handleDownloadClassificationDataAPI(params);

            // Create a Blob from the response data
            const blob = new Blob([response.data], { type: 'application/zip' });

            // Create a temporary URL and trigger download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `dataset_${model_data_info_id}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            console.log('ZIP file downloaded successfully');
            return { status: 'success' };
        } catch (error) {
            console.error('Download failed:', error);
            swalFail('Download failed', error.message || 'Unable to download classification data.');
            return { status: 'error', message: error.message };
        }
    };

    const handleStartClassificationTraining = async (trainingConfig = {}) => {
        const { classes, unstructuredImages } = useClassificationStore.getState();
        const currentModelDataInfoId = modelDataInfo?._id || modelDataInfo?.id || modelDataInfo?.model_data_info_id;

        const hasClassImages = Array.isArray(classes) && classes.some((c) => Array.isArray(c.images) && c.images.length > 0);
        const hasUnstructuredImages = Array.isArray(unstructuredImages) && unstructuredImages.length > 0;
        if (!hasClassImages && !hasUnstructuredImages) {
            swalFail("Dataset Required", "Upload or assign at least one image before training.");
            return false;
        }

        if (!currentModelDataInfoId) {
            swalFail("Dataset Required", "Please save the dataset or load an existing version before starting training.");
            return false;
        }

        const isPrivate = (modelInfo?.visibility || "private").toLowerCase() === "private";
        const confirmTitle = isPrivate ? "Payment Required" : "Start Training?";
        const confirmText = isPrivate
            ? "Private model training requires a $5 payment. Make your model public (Model Management → Edit) for free training, or proceed to pay $5 and start training."
            : "YOLO classification training will run in the background using the current dataset.";

        let confirmation;
        if (isPrivate) {
            confirmation = await Swal.fire({
                title: confirmTitle,
                text: confirmText,
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Proceed with $5",
                cancelButtonText: "Cancel",
            });
        } else {
            confirmation = await new Promise((resolve) => {
                swalConfirm(confirmTitle, confirmText, resolve);
            });
        }
        if (!confirmation?.isConfirmed) {
            return false;
        }

        Swal.fire({
            title: "Saving dataset and starting training...",
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        let persistedModelDataInfoId = currentModelDataInfoId;
        try {
            persistedModelDataInfoId = await handleSaveClassificationData({
                forceUpdateExisting: true,
                silent: true,
                skipNavigate: true,
            });
        } catch (saveError) {
            Swal.close();
            swalFail("Save Failed", saveError.message || "Failed to save dataset before training.");
            return false;
        }

        if (!persistedModelDataInfoId) {
            Swal.close();
            swalFail("Save Failed", "Dataset could not be saved before training.");
            return false;
        }

        const clampPercent = (value, fallback) => {
            const numeric = Number(value);
            if (Number.isNaN(numeric)) return fallback;
            return Math.max(0, Math.min(100, Math.round(numeric)));
        };

        const normalizedConfig = {
            modelVariant: trainingConfig?.modelVariant || "yolov8n-cls.pt",
            epochs: Math.min(Math.max(Number(trainingConfig?.epochs) || 5, 1), 100),
            imgsz: Math.max(Number(trainingConfig?.imgsz) || 224, 32),
            batchSize: Math.max(1, Number(trainingConfig?.batchSize) || 16),
            stratify: !!trainingConfig?.stratify,
            trainSplit: clampPercent(trainingConfig?.trainSplit, 70),
            valSplit: clampPercent(trainingConfig?.valSplit, 20),
            testSplit: clampPercent(trainingConfig?.testSplit, 10),
            enableEarlyStopping: !!trainingConfig?.enableEarlyStopping,
            patience: trainingConfig?.enableEarlyStopping ? Math.max(1, Math.min(200, Number(trainingConfig?.patience) || 50)) : null,
        };

        const splitSum = normalizedConfig.trainSplit + normalizedConfig.valSplit + normalizedConfig.testSplit;
        if (splitSum !== 100 && splitSum > 0) {
            normalizedConfig.trainSplit = Math.round((normalizedConfig.trainSplit / splitSum) * 100);
            normalizedConfig.valSplit = Math.round((normalizedConfig.valSplit / splitSum) * 100);
            normalizedConfig.testSplit = Math.max(0, 100 - (normalizedConfig.trainSplit + normalizedConfig.valSplit));
        }

        if (normalizedConfig.trainSplit < 50) {
            normalizedConfig.trainSplit = 50;
            const remaining = 50;
            const valRatio = normalizedConfig.valSplit / (normalizedConfig.valSplit + normalizedConfig.testSplit || 1);
            normalizedConfig.valSplit = Math.round(remaining * valRatio);
            normalizedConfig.testSplit = Math.max(0, 100 - (normalizedConfig.trainSplit + normalizedConfig.valSplit));
        }

        const payload = {
            model_data_info_id: persistedModelDataInfoId,
            epochs: normalizedConfig.epochs,
            imgsz: normalizedConfig.imgsz,
            model_name: normalizedConfig.modelVariant,
            batch_size: normalizedConfig.batchSize,
            stratify: normalizedConfig.stratify ? 1 : 0,
            train_split: normalizedConfig.trainSplit,
            val_split: normalizedConfig.valSplit,
            test_split: normalizedConfig.testSplit,
        };

        if (normalizedConfig.enableEarlyStopping && normalizedConfig.patience) {
            payload.patience = normalizedConfig.patience;
        }

        // Persist training config immediately so UI can show it even before queueing
        const sanitizePayload = (data) =>
            Object.entries(data).reduce((acc, [key, value]) => {
                if (value !== undefined && value !== null) {
                    acc[key] = value;
                }
                return acc;
            }, {});

        try {
            await saveClassificationTrainingConfigAPI(
                sanitizePayload({
                    model_data_info_id: persistedModelDataInfoId,
                    model_type: "classification",
                    model_name: normalizedConfig.modelVariant,
                    epochs: normalizedConfig.epochs,
                    imgsz: normalizedConfig.imgsz,
                    batch_size: normalizedConfig.batchSize,
                    stratify: normalizedConfig.stratify ? 1 : 0,
                    train_split: normalizedConfig.trainSplit,
                    val_split: normalizedConfig.valSplit,
                    test_split: normalizedConfig.testSplit,
                    patience: normalizedConfig.enableEarlyStopping ? normalizedConfig.patience : undefined,
                })
            );
        } catch (e) {
            // Non-blocking: continue to queue training even if this fails
            console.warn("Failed to save training config preflight:", e?.message || e);
        }

        try {
            const response = await startClassificationTrainingAPI(sanitizePayload(payload));
            Swal.close();

            const { status, message } = response?.data || {};
            if (status === "success") {
                await Swal.fire({
                    title: "Training Started",
                    text: message || "Training has started in the background.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                    timerProgressBar: true,
                });
                navigate("/ModelManagement", {
                    state: { projectId: modelInfo?.projectId || modelInfo?.project_id },
                });
            } else {
                swalFail("Training Failed", message || "Unable to start training.");
            }
            return true;
        } catch (error) {
            Swal.close();
            const errorMessage = error?.response?.data?.message || error.message || "An error occurred while starting training.";
            swalFail("Error", errorMessage);
            return false;
        }
    };

    // =========================================================================
    // EXPORT HANDLERS OBJECT
    // =========================================================================

    const handlers = {
        handleUnstructuredUpload,
        handleClassUpload,
        handleInitializeClasses,
        handleAddClasses,
        handleReduceClasses,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileInput,
        handleClassNameChange,
        handleImageClick,
        handleClassButtonClick,
        findImageById,
        assignImageToClass,
        moveImageToUnstructured,
        autoAdvanceToNextImage,
        toggleImageSelection,
        applySelection,
        handleImageDragStart,
        moveImagesToClass,
        moveImagesToUnstructured,
        assignMultipleToClass,
        selectAllUnstructured,
        clearSelection,
        deleteImage,
        deleteSelectedImages,
        handleSaveClassificationData,
        handleGetOneClassificationData,
        handleResetClsData,
        handleDownloadClassificationData,
        handleStartClassificationTraining,
    };

    return {
        classes,
        setClasses,
        classCount,
        setClassCount,
        unstructuredImages,
        selectedImage,
        setSelectedImage,
        selectedImages,
        setSelectedImages,
        dragOver,
        setDragOver,
        activeDragClass,
        setActiveDragClass,
        showDeleteButton,
        setShowDeleteButton,
        isUploading,
        uploadProgress,
        completedUploads,
        totalUploads,
        handlers
    };
};

export default useClassificationLabelHook;